<template>
    <JetDialogModal :show="showModal" @close="closeModal" max-width="2xl">
        <template #title>
            Modifier le client du colis N°{{ currentPackage?.code }}
        </template>

        <template #content>
            <div v-if="currentPackage" class="text-lg font-medium text-lg font-medium mb-4 mt-2 text-gray-600 p-2 bg-gray-200 rounded-md border border-gray-300">
                <b>Client actuel: </b>
                <span class="font-semibold text-sky">{{ currentPackage.sender_real_name }}</span>
                <span v-if="currentPackage.sender_real_phone" class="text-gray-400">
                    | {{ currentPackage.sender_real_phone }}
                </span>
            </div>

            <ul class="text-sm font-medium flex flex-nowrap overflow-x-scroll no-scrollbar mb-2">
                <li class="pb-3 mr-6 last:mr-0 last:pr-4 sm:last:pr-6 lg:last:pr-8">
                    <a class="whitespace-nowrap cursor-pointer uppercase" @click.prevent="currentTab = 1"
                        :class="currentTab === 1 ? 'text-sky hover:text-sky-400 font-bold' : 'text-gray-500 hover:text-gray-600'"
                    >Client existant</a>
                </li>
                <li class="pb-3 mr-6 last:mr-0 first:pl-4 sm:first:pl-6 lg:first:pl-8 last:pr-4 sm:last:pr-6 lg:last:pr-8">
                    <a class="whitespace-nowrap cursor-pointer uppercase" @click.prevent="currentTab = 2"
                        :class="currentTab === 2 ? 'text-sky hover:text-sky-400 font-bold' : 'text-gray-500 hover:text-gray-600'"
                    >Nouveau client</a>
                </li>
            </ul>

            <hr class="mb-3">

            <div v-if="currentTab === 1">
                <JetLabelRequired for="sender_id" value="Client" />
                <Multiselect
                    id="sender_id"
                    :delay="0"
                    :minChars="1"
                    :searchable="true"
                    :valueProp="'id'"
                    :trackBy="'id'"
                    :filterResults="false"
                    :resolveOnLoad="true"
                    v-model="form.sender_id"
                    placeholder="Tapez pour rechercher le client"
                    :options="async function(query) {
                        return await fetchUser(query)
                    }"
                >
                    <template v-slot:singlelabel="{ value }">
                        <div class="multiselect-single-label text-sky">
                            {{ value.name + (value.email ? ' | ' + value.email : '') + (value.full_phone ? ' | ' + value.full_phone : '') }}
                        </div>
                    </template>
                    <template v-slot:option="{ option }">
                        {{ option.name + (option.email ? ' | ' + option.email : '') + (option.full_phone ? ' | ' + option.full_phone : '') }}
                    </template>
                </Multiselect>
                <JetInputError :message="form.errors.sender_id" />
            </div>

            <div v-else class="grid grid-cols-2 gap-4">
                <div class="col-span-2">
                    <JetLabelRequired for="sender_name" value="Nom du client" />
                    <JetInput id="sender_name" type="text" autocomplete="off"
                        v-model="form.sender_name" placeholder="Entrez le nom du client"
                    />
                    <JetInputError :message="form.errors.sender_name" />
                </div>
                <div class="col-span-1">
                    <JetLabel for="sender_phone" value="Téléphone du client:" />
                    <vue-tel-input required @input="senderPhoneInput"
                        :defaultCountry="form.sender_country_code"
                        :value="form.sender_phone"
                    />
                    <JetInputError :message="form.errors.sender_phone" />
                </div>
                <div class="col-span-1">
                    <JetLabel for="sender_email" value="Email du client:" />
                    <JetInput id="sender_email" type="text" autocomplete="off"
                        v-model="form.sender_email" placeholder="Entrez l'email du client"
                    />
                    <JetInputError :message="form.errors.sender_email" />
                </div>
            </div>
        </template>

        <template #footer>
            <BotiButton @click="closeModal" text="Quitter" color="red">
                <template #icon><i class="fad fa-times-circle text-sm ml-1"></i></template>
            </BotiButton>

            <BotiButton :loader="form.processing" :disabled="form.processing"
                @click="save" text="Modifier le client" color="sky"
                class="ml-2" :class="{ 'opacity-25': form.processing }"
            >
                <svg v-if="form.processing" class="animate-spin w-4 h-4 fill-current shrink-0" viewBox="0 0 16 16">
                    <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z"></path>
                </svg>
                <template #icon>
                    <i class="fad fa-user-edit text-sm ml-1"></i>
                </template>
            </BotiButton>
        </template>
    </JetDialogModal>
</template>

<script>
    import { defineComponent } from 'vue'
    import JetLabel from '@/Jetstream/Label'
    import JetInput from '@/Jetstream/Input'
    import BotiButton from '@/Components/Button'
    import Multiselect from '@vueform/multiselect'
    import JetInputError from '@/Jetstream/InputError'
    import JetDialogModal from '@/Jetstream/DialogModal'
    import FetchUserMixins from '@/Mixins/FetchUserMixins'
    import JetLabelRequired from '@/Jetstream/LabelRequired'

    export default defineComponent({
        components: {
            JetLabel,
            JetInput,
            BotiButton,
            Multiselect,
            JetInputError,
            JetDialogModal,
            JetLabelRequired,
        },

        mixins: [FetchUserMixins],

        props: {
            travel: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                showModal: false,
                currentTab: 1,
                currentPackage: null,
                form: this.$inertia.form({
                    sender_id: null,
                    sender_name: null,
                    sender_email: null,
                    sender_phone: null,
                    sender_dial_code: null,
                    sender_country_code: null,
                }),
            }
        },

        created() {
            this.emitter.on('showChangePackageClientModal', (pack) => {
                this.open(pack)
            })
        },

        methods: {
            senderPhoneInput(number, phoneObject) {
                if (number !== undefined && phoneObject !== undefined) {
                    this.form.sender_phone = number ? number : ''
                    this.form.sender_dial_code = phoneObject ? phoneObject.country.dialCode : null
                    this.form.sender_country_code = phoneObject ? phoneObject.country.iso2 : null
                }
            },

            open(pack) {
                this.currentPackage = pack
                this.currentTab = 1
                this.form.reset()
                this.form.clearErrors()

                if (pack?.sender_id) {
                    this.form.sender_id = pack.sender_id
                }

                this.showModal = true
            },

            closeModal() {
                this.showModal = false
                this.currentPackage = null
                this.form.clearErrors()
                this.form.reset()
            },

            save() {
                this.form
                    .transform((data) => ({
                        ...data,
                        type: this.currentTab,
                    }))
                    .put(route('travels.packages.client', {
                        travel: this.travel,
                        package: this.currentPackage,
                    }), {
                        preserveScroll: true,
                        onSuccess: () => {
                            this.closeModal()
                            this.$swal.fire(
                                'Colis',
                                'Client du colis mis à jour avec succès.',
                                'success'
                            )
                        },
                    })
            },
        },
    })
</script>
